<template>
  <div
    v-if="visible"
    class="dev-host-select-wrap"
  >
    <span class="dev-host-select-label">Host</span>
    <AntSelect
      class="dev-host-select"
      :value="modelValue"
      :options="hostOptions"
      placeholder="Host"
      :disabled="!hostOptions.length"
      :getPopupContainer="trigger => trigger.parentNode"
      @change="onChange"
    />
  </div>
</template>

<script>
import {
  ref,
  inject,
  computed,
  onBeforeMount,
} from 'vue'

import { Select as AntSelect } from 'ant-design-vue'

import {
  shouldShowDevHostSelect,
  ensureTemplatesListLoaded,
  buildHostOptionsFromTemplates,
  setResolvedBrandByHost,
  getResolvedBrand,
  applyFavicon,
  getCurrentDevOrWindowHost,
} from '@/libs/brandFromTemplates'

const LEGACY_STORAGE_KEY = 'apiDocV3Host'

export { shouldShowDevHostSelect }

export default {
  name: 'DevHostSelect',
  components: {
    AntSelect,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    selectedBrand: {
      type: String,
      default: '',
    },
    storageKey: {
      type: String,
      default: 'devHost',
    },
  },
  emits: ['update:modelValue', 'update:selectedBrand', 'change', 'ready'],
  setup(props, { emit }) {
    const isDev = inject('isDev', ref(false))
    const nodeEnv = inject('nodeEnv')

    const hostOptions = ref([])
    /** host → template 摘要（含 brandText／swaPlatforms 等） */
    const templateMetaByHost = ref({})

    const visible = computed(() => shouldShowDevHostSelect(isDev.value, nodeEnv.value))

    const readStoredHost = () => (
      localStorage.getItem(props.storageKey)
      || localStorage.getItem(LEGACY_STORAGE_KEY)
      || ''
    )

    const persistHost = (value) => {
      if (!value) return
      localStorage.setItem(props.storageKey, value)
      if (props.storageKey !== LEGACY_STORAGE_KEY) {
        localStorage.setItem(LEGACY_STORAGE_KEY, value)
      }
    }

    const resolveSelectedBrand = hostValue => templateMetaByHost.value[hostValue]?.brandText || ''

    const applyHostSideEffects = (value) => {
      const resolved = setResolvedBrandByHost(value)
      applyFavicon(resolved.faviconUrl)
      return resolved
    }

    const setHost = (value, { emitChange = false } = {}) => {
      const resolved = applyHostSideEffects(value)
      const nextSelectedBrand = resolved.brandText || resolveSelectedBrand(value)
      emit('update:modelValue', value)
      emit('update:selectedBrand', nextSelectedBrand)
      if (emitChange) {
        emit('change', value, {
          selectedBrand: nextSelectedBrand,
          template: resolved.template || templateMetaByHost.value[value] || null,
        })
      }
    }

    const syncOptionsFromCache = () => {
      const { hostOptions: options, metaByHost } = buildHostOptionsFromTemplates()
      hostOptions.value = options
      templateMetaByHost.value = metaByHost
      return options.map(({ value }) => value)
    }

    const loadHostOptions = async () => {
      await ensureTemplatesListLoaded()
      const hosts = syncOptionsFromCache()
      const current = props.modelValue || readStoredHost() || getCurrentDevOrWindowHost()
      const nextHost = hosts.includes(current) ? current : (hosts[0] || current)
      if (nextHost) {
        persistHost(nextHost)
        setHost(nextHost)
      }
      return nextHost || ''
    }

    const onChange = (value) => {
      persistHost(value)
      setHost(value, { emitChange: true })
    }

    onBeforeMount(async () => {
      // 生產不顯示 Host 下拉：沿用 bootstrap by-host 結果，勿被 localStorage DevHost 覆寫
      if (!visible.value) {
        const resolved = getResolvedBrand()
        const host = resolved.host || window.location.host
        emit('update:modelValue', host)
        emit('update:selectedBrand', resolved.brandText || '')
        emit('ready', host, {
          selectedBrand: resolved.brandText,
          template: resolved.template,
        })
        return
      }

      let resolvedHost = props.modelValue || readStoredHost()
      if (!props.modelValue && resolvedHost) setHost(resolvedHost)
      resolvedHost = await loadHostOptions() || resolvedHost
      const resolved = setResolvedBrandByHost(resolvedHost)
      emit('ready', resolvedHost, {
        selectedBrand: resolved.brandText,
        template: resolved.template,
      })
    })

    return {
      visible,
      hostOptions,
      onChange,
    }
  },
}
</script>

<style>
.dev-host-select-wrap {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.dev-host-select-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

.dev-host-select {
  width: 280px;
}

@media print {
  .dev-host-select-wrap {
    display: none;
  }
}
</style>
